import { authUser, fetchAccessToken } from "@origin-digital/event-dispatcher";
import type { UnauthInternetChatAttachment } from "./index";

export const isUserAuthenticated = async (): Promise<boolean> => {
  try {
    const token = await fetchAccessToken({});
    if (token?.accessToken) return true;
  } catch (_) {
    // Fall back to the authUser check.
  }

  const authInfo = await authUser({});
  return authInfo ? authInfo.isAuth : false;
};

/**
 * Temporary TAL policy. Remove this wrapper to restore pathname-only chat.
 * The listener belongs to the page integration, so logout can reattach chat.
 */
export function startUnauthInternetChatAuthGate(
  attach: (
    canAttach: () => boolean
  ) => Promise<UnauthInternetChatAttachment | undefined>
): () => Promise<void> {
  let disposed = false;
  let eligible = false;
  let checking = false;
  let checkRequested = false;
  let attachRequested = false;
  let attaching: Promise<void> | undefined,
    attachment: UnauthInternetChatAttachment | undefined;
  const canAttach = (): boolean => !disposed && eligible;
  const detach = (): void => {
    attachment?.detach();
    attachment = undefined;
  };

  const attachIfEligible = (): void => {
    if (attachment?.isDetached) attachment = undefined;
    if (!canAttach() || attachment) return;
    if (attaching) {
      attachRequested = true;
      return;
    }
    attaching = attach(canAttach)
      .then((next) => {
        if (canAttach()) attachment = next;
        else next?.detach();
      })
      .catch(() => {
        // Keep unexpected attachment failures isolated; a later check can retry.
      })
      .finally(() => {
        attaching = undefined;
        if (attachRequested) {
          attachRequested = false;
          attachIfEligible();
        }
      });
  };

  const check = async (): Promise<void> => {
    if (disposed) return;
    checkRequested = true;
    eligible = false;
    if (checking) return;
    checking = true;
    checkRequested = false;
    try {
      const authenticated = await isUserAuthenticated();
      if (!disposed && !checkRequested) {
        eligible = !authenticated;
        if (authenticated) detach();
        else attachIfEligible();
      }
    } catch {
      // Unknown auth must not expose unauth chat; retry on a later return.
      detach();
    } finally {
      checking = false;
      if (checkRequested) check();
    }
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  check();

  return () => {
    disposed = true;
    eligible = false;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    detach();
    // A replacement page owner must wait until stale pending handles detach.
    return attaching ?? Promise.resolve();
  };
}
